/**
 * SchedulingRulesPanel Component
 *
 * CRUD interface for production scheduling rules. Each rule defines a
 * hard or soft constraint used by the schedule engine to nudge biological
 * dates onto the human calendar.
 *
 * US-PPL-001 Phase 5: Scheduling Rules Configuration
 */

import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useSchedulingRules, useSchedulingRuleMutations } from '@/hooks/useSchedulingRules'
import RuleFormDialog from './RuleFormDialog'
import RuleImpactPreview from './RuleImpactPreview'
import { Plus, Pencil, Trash2, Eye, Shield, ShieldAlert, Loader2 } from 'lucide-react'

const DAY_LABELS = { 0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat' }

const PHASE_LABELS = {
  lc_incubation: 'LC Incubation', spawn_run: 'Spawn Run',
  substrate_incubation: 'Incubation', fruiting: 'Fruiting',
  rest_period: 'Rest', other: 'Other'
}

function formatRuleParams(ruleType, params) {
  if (!params) return ''

  switch (ruleType) {
    case 'avoid_day':
    case 'prefer_day': {
      const days = (params.days || []).map(d => DAY_LABELS[d] || d).join(', ')
      const tasks = (params.task_types || []).map(t => t.replace('_', ' ')).join(', ')
      const verb = ruleType === 'avoid_day' ? 'Avoid' : 'Prefer'
      return `${verb} ${days} for ${tasks}`
    }
    case 'max_gap':
      return `Max ${params.max_days || '?'} days from ${PHASE_LABELS[params.from_phase_type] || params.from_phase_type} to ${PHASE_LABELS[params.to_phase_type] || params.to_phase_type}`
    case 'min_gap':
      return `Min ${params.min_days || '?'} days from ${PHASE_LABELS[params.from_phase_type] || params.from_phase_type} to ${PHASE_LABELS[params.to_phase_type] || params.to_phase_type}`
    case 'capacity_limit':
      return `Max ${params.max_tasks || '?'} tasks per day`
    default:
      return JSON.stringify(params)
  }
}

export default function SchedulingRulesPanel() {
  const { rules, loading, refetch } = useSchedulingRules()
  const { upsertRule, deleteRule, toggleRule, previewRuleChange, loading: mutating } = useSchedulingRuleMutations()

  const [showDialog, setShowDialog] = useState(false)
  const [editingRule, setEditingRule] = useState(null)
  const [deletingRule, setDeletingRule] = useState(null)
  const [previewingRuleId, setPreviewingRuleId] = useState(null)
  const [previewData, setPreviewData] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const handleCreate = () => {
    setEditingRule(null)
    setShowDialog(true)
  }

  const handleEdit = (rule) => {
    setEditingRule(rule)
    setShowDialog(true)
  }

  const handleSave = async (formData) => {
    try {
      await upsertRule(formData)
      setShowDialog(false)
      setEditingRule(null)
      refetch()
    } catch {
      // error handled by hook
    }
  }

  const handleDelete = async () => {
    if (!deletingRule) return
    try {
      await deleteRule(deletingRule.id)
      setDeletingRule(null)
      refetch()
    } catch {
      // error handled by hook
    }
  }

  const handleToggle = async (rule, isActive) => {
    try {
      await toggleRule(rule.id, isActive)
      refetch()
    } catch {
      // error handled by hook
    }
  }

  const handlePreview = async (rule) => {
    if (previewingRuleId === rule.id) {
      setPreviewingRuleId(null)
      setPreviewData(null)
      return
    }

    setPreviewingRuleId(rule.id)
    setPreviewLoading(true)
    try {
      const data = await previewRuleChange(rule.id, null, null)
      setPreviewData(data)
    } catch {
      setPreviewData(null)
    } finally {
      setPreviewLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-24 w-full rounded-lg" />
        ))}
      </div>
    )
  }

  const hardRules = rules.filter(r => r.constraint_type === 'hard')
  const softRules = rules.filter(r => r.constraint_type === 'soft')

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Rules define how the schedule engine nudges biological dates onto the human calendar.
        </p>
        <Button size="sm" onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-1" />
          Add Rule
        </Button>
      </div>

      {rules.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Shield className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No scheduling rules defined</p>
            <p className="text-sm mt-1">Add rules to control how production dates are scheduled</p>
          </CardContent>
        </Card>
      )}

      {hardRules.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium flex items-center gap-1.5">
            <ShieldAlert className="h-4 w-4 text-red-500" />
            Hard Constraints
          </h3>
          {hardRules.map(rule => (
            <RuleCard
              key={rule.id}
              rule={rule}
              onEdit={handleEdit}
              onDelete={setDeletingRule}
              onToggle={handleToggle}
              onPreview={handlePreview}
              showPreview={previewingRuleId === rule.id}
              previewData={previewingRuleId === rule.id ? previewData : null}
              previewLoading={previewingRuleId === rule.id && previewLoading}
            />
          ))}
        </div>
      )}

      {softRules.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium flex items-center gap-1.5">
            <Shield className="h-4 w-4 text-blue-500" />
            Soft Preferences
          </h3>
          {softRules.map(rule => (
            <RuleCard
              key={rule.id}
              rule={rule}
              onEdit={handleEdit}
              onDelete={setDeletingRule}
              onToggle={handleToggle}
              onPreview={handlePreview}
              showPreview={previewingRuleId === rule.id}
              previewData={previewingRuleId === rule.id ? previewData : null}
              previewLoading={previewingRuleId === rule.id && previewLoading}
            />
          ))}
        </div>
      )}

      <RuleFormDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        rule={editingRule}
        onSave={handleSave}
        saving={mutating}
      />

      <AlertDialog open={!!deletingRule} onOpenChange={() => setDeletingRule(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingRule?.rule_name}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function RuleCard({ rule, onEdit, onDelete, onToggle, onPreview, showPreview, previewData, previewLoading }) {
  return (
    <Card className={!rule.is_active ? 'opacity-60' : ''}>
      <CardContent className="py-3 px-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-sm">{rule.rule_name}</span>
              <Badge
                variant={rule.constraint_type === 'hard' ? 'destructive' : 'default'}
                className="text-[10px] px-1.5 py-0"
              >
                {rule.constraint_type}
              </Badge>
              <span className="text-xs text-muted-foreground">rank {rule.priority_rank}</span>
            </div>

            {rule.description && (
              <p className="text-xs text-muted-foreground mb-1">{rule.description}</p>
            )}

            <p className="text-xs font-mono text-muted-foreground">
              {formatRuleParams(rule.rule_type, rule.parameters)}
            </p>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onPreview(rule)} title="Preview impact">
              <Eye className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(rule)} title="Edit">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => onDelete(rule)} title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
            <Switch
              checked={rule.is_active}
              onCheckedChange={(v) => onToggle(rule, v)}
              className="ml-1"
            />
          </div>
        </div>

        {showPreview && (
          <RuleImpactPreview preview={previewData} loading={previewLoading} />
        )}
      </CardContent>
    </Card>
  )
}
